// Routing
import {
  browserHistory,
  IndexRedirect,
  IndexRoute,
  Redirect,
  Route,
  Router,
} from 'react-router';
import type { RouteComponent } from 'react-router';

import {
  API_WEBHOOK_NOTIFICATIONS,
  ARCHIVE_AND_LICENSE_UPLOAD,
  BINARY_DECOMPOSITION,
  INVENTORY_UI,
  LICENSE_CONCLUDED,
  PACKAGE_INVENTORY,
  RAW_VULN_EDIT,
  REPORTS_V3_NEW_UX_FLOW,
  SBOM_PORTAL,
  SBOM_PORTAL_PRIVATE,
  SBOM_SHARING,
  SNIPPET_DETECTION,
  TEAM_GROUPS,
  VENDORED_DEPENDENCY_DETECTION,
} from '../shared/featureFlags/flagNames';
import { userHasPermission } from '../shared/permissions';
import { isFreeTier } from '../shared/subscriptions';
import { Action, ResourceType } from '../typings/permissions';

import Application from './Application';
import JoinOrganization from './components/accounts/JoinOrganization';
import AdminDashboard from './components/admin/AdminDashboard';
import Auth from './components/auth';
import AuthLogin from './components/auth/pages/Login';
import AuthOnboarding from './components/auth/pages/Onboarding';
import AuthPasswordReset from './components/auth/pages/PasswordReset';
import AuthRegister from './components/auth/pages/Register';
import Dashboard from './components/Dashboard';
import ReportsDashboard from './components/dashboard/ReportsDashboard';
import { FeatureFlagRequired } from './components/generic';
import AccessGate from './components/generic/Gates/AccessGate';
import NotFound from './components/generic/NotFound';
import InternalToolsPage from './components/internal_tools/InternalToolsPage';
import IssueOverview from './components/issueOverview/IssueOverview';
import IssueComparisonContainer from './components/issues/IssueComparison/IssueComparisonContainer';
import IssueDetails from './components/issues/IssueDetails/IssueDetailsV2';
import IssueExceptionsPage from './components/issues/IssueExceptions';
import IssuesContainer from './components/issues/IssuesContainer';
import IssuesInbox from './components/issues/IssuesInbox';
import ProjectIssuesRouteContainer from './components/issues/ProjectIssuesRouteContainer';
import ReleaseGroupIssuesContainer from './components/issues/ReleaseGroupIssuesContainer';
import PackageInventoryContainer from './components/packageInventory/PackageInventoryContainer';
import PackageVersionContainer, { ProjectsTable } from './components/packageInventory/PackageVersion';
import VulnerabilitiesTable from './components/packageInventory/PackageVersion/VulnerabilitiesTable';
import DisplayLicensePolicyPageV2 from './components/policies/license/LicensePolicyPage';
import DisplayLicensePolicyRulesPage from './components/policies/license/LicensePolicySettingsPage';
import ListLicensePoliciesPage from './components/policies/ListLicensePoliciesPage';
import ListPoliciesPage from './components/policies/ListPoliciesPage';
import ListQualityPoliciesPage from './components/policies/ListQualityPoliciesPage';
import ListSbomPoliciesPage from './components/policies/ListSbomPoliciesPage';
import ListSecurityPoliciesPage from './components/policies/ListSecurityPoliciesPage';
import DisplayQualityPolicyPage from './components/policies/quality/DisplayQualityPolicyPage';
import DisplaySBOMPolicyPage from './components/policies/sbom/DisplaySBOMPolicyPage';
import DisplaySecurityPolicyPage from './components/policies/security/DisplaySecurityPolicyPage';
import ProjectBrowseLicensesPage from './components/projects/browse/ProjectBrowseLicensesPage';
import ProjectDashboard from './components/projects/dashboardV2';
import DependenciesV2 from './components/projects/dependenciesV2';
import ReportGeneration from './components/projects/generic/ReportGenerationPage';
import ProjectGroupReportContainer from './components/projects/generic/ReportGenerationV2/context/ProjectGroupReportContainer';
import ProjectReportContainer from './components/projects/generic/ReportGenerationV2/context/ProjectReportContainer';
import ProjectReportGenerationSettings from './components/projects/generic/ReportGenerationV2/projectLevel/Settings';
import ReleaseGroupReportGenerationSettings from './components/projects/generic/ReportGenerationV2/releaseGroupLevel/Settings';
import BulkImportPage from './components/projects/import/BulkImportPage';
import BinaryAnalysisContainer from './components/projects/inventory/binary/BinaryAnalysisContainer';
import ProjectInventoryBrowseContainer from './components/projects/inventory/ProjectInventoryBrowseContainer';
import ReleaseGroupInventoryBrowseContainer from './components/projects/inventory/ReleaseGroupInventoryBrowseContainer';
import ProjectOverviewPage from './components/projects/overview';
import ProjectSummary from './components/projects/overview/ProjectSummary';
import ProjectPreview from './components/projects/preview';
import ProjectGroupContainer from './components/projects/project_grouping/ProjectGroupContainer';
import ProjectGroupDependencyValidityContainer from './components/projects/project_grouping/ProjectGroupDependencyValidityContainer';
import ProjectGroupLicenses from './components/projects/project_grouping/ProjectGroupLicenses';
import ProjectGroupProjectsTab from './components/projects/project_grouping/ProjectGroupProjectsTab';
import ProjectGroupReleaseSummary from './components/projects/project_grouping/ProjectGroupReleaseSummary';
import CloneReleaseContainer from './components/projects/project_grouping/revamp/containers/CloneReleaseContainer/CloneReleaseContainer';
import CreateGroupContainer from './components/projects/project_grouping/revamp/containers/CreateGroupContainer/CreateGroupContainer';
import EditReleaseContainer from './components/projects/project_grouping/revamp/containers/EditReleaseContainer/EditReleaseContainer';
import ReleaseGroupReleasesList from './components/projects/project_grouping/revamp/releases';
import ReleaseGroupsSettings from './components/projects/project_grouping/revamp/settings';
import ReleaseGroupsSettingsGeneral from './components/projects/project_grouping/revamp/settings/General';
import ReleaseGroupsSettingsPortal from './components/projects/project_grouping/revamp/settings/Portal';
import ProjectBuildsPage from './components/projects/ProjectBuildsPage/index';
import ProjectContainer from './components/projects/ProjectContainer';
import ProjectEmpty from './components/projects/ProjectEmpty';
import RevisionContainer from './components/projects/RevisionContainer';
import { RevisionReadyContainer } from './components/projects/RevisionReadyContainer';
import ProjectSettingsV2Container from './components/projects/settingsv2';
import ProjectSettingsV2Builds from './components/projects/settingsv2/builds';
import ProjectSettingsV2General from './components/projects/settingsv2/general';
import ProjectSettingsV2IssueTracker from './components/projects/settingsv2/issue-tracker';
import ProjectSettingsV2Notifications from './components/projects/settingsv2/notifications';
import ProjectSettingsV2Policies from './components/projects/settingsv2/policies';
import ProjectSettingsV2UpdateHooks from './components/projects/settingsv2/update-hooks';
import SnippetsContainer from './components/projects/snippets';
import SnippetsCompareContainer from './components/projects/snippets/compare';
import FullPageSnippetDetails from './components/projects/snippets/FullPageSnippetDetails';
import ArchiveUploadPage from './components/projects/upload/ArchiveUploadPage';
import BinaryUploadPage from './components/projects/upload/BinaryUploadPage';
import SBOMUploadPage from './components/projects/upload/SBOMUploadPage';
import VendoredDependenciesContainer from './components/projects/vendoredDependencies';
import ProjectProvider from './components/providers/ProjectProvider';
import RawVulnEditPage from './components/raw_vulns/RawVulnEditPage';
import { ReportsV3Container, ReportsV3Page } from './components/reportsv3';
import EnsureWebhook from './components/services/EnsureWebhook';
import SettingsContainer from './components/settings';
import LoginOrganizationSettings from './components/settings/account';
import AuditLogs from './components/settings/audit-logs';
import AuditLogsPage from './components/settings/audit-logs/AuditLogsPage';
import IntegrationSettings from './components/settings/integrations';
import APIGranularTokenSettings from './components/settings/integrations/apiTokens';
import ApiWebhookSettings from './components/settings/integrations/apiWebhooks';
import BitbucketSettings from './components/settings/integrations/BitbucketSettings';
import CreateApiUserSettings from './components/settings/integrations/CreateApiUserSettings';
import CustomLicenseScanSettings from './components/settings/integrations/customLicenseScans';
import GithubSettings from './components/settings/integrations/GithubSettings';
import GitlabSettings from './components/settings/integrations/GitlabSettings';
import JiraOrganizationSettings from './components/settings/integrations/jira';
import OIDCProviderSettings from './components/settings/integrations/oidcProviders';
import SlackSettings from './components/settings/integrations/slack';
import LanguageSettings from './components/settings/languages';
import BowerLanguageSettings from './components/settings/languages/BowerSettings';
import CocoaLanguageSettings from './components/settings/languages/CocoaSettings';
import DotnetLanguageSettings from './components/settings/languages/DotnetSettings';
import GemLanguageSettings from './components/settings/languages/GemSettings';
import GitLanguageSettings from './components/settings/languages/GitSettings';
import JavaScriptLanguagesSettings from './components/settings/languages/JavascriptSettings';
import JavaLanguagesSettings from './components/settings/languages/JavaSettings';
import PipLanguageSettings from './components/settings/languages/PipSettings';
import PubLanguagesSettings from './components/settings/languages/PubSettings';
import OrganizationSettings from './components/settings/organization';
import AuthenticationSettings from './components/settings/organization/authentication';
import OrganizationBanners from './components/settings/organization/banners';
import OrganizationGeneralSettings from './components/settings/organization/general';
import {
  SettingsOrganizationPortalConfiguration,
  SettingsOrganizationPortalTokens,
} from './components/settings/organization/portal';
import OrganizationRolesEdit from './components/settings/organization/roles/OrganizationRolesEdit';
import OrganizationRolesIndex from './components/settings/organization/roles/OrganizationRolesIndex';
import OrganizationTeamsIndex from './components/settings/organization/teams/OrganizationTeamsIndex';
import TeamGroupManagement from './components/settings/organization/teams/teamGroups/management';
import TeamSettings from './components/settings/organization/teams/TeamSettings';
import OrganizationUsersIndex from './components/settings/organization/users';
import EditUserSettings from './components/settings/organization/users/EditUsersPage';
import SettingsOrganizationProject from './components/settings/project';
import SettingsOrganizationProjectGeneral from './components/settings/project/general';
import SettingsOrganizationProjectIssues from './components/settings/project/issues';
import SettingsOrganizationProjectNotifications from './components/settings/project/notifications';
import SettingsOrganizationProjectUpdateHooks from './components/settings/project/update-hooks';
import SubscriptionSettings from './components/settings/subscription';
import PlanUpdateCallback from './components/settings/subscription/PlanUpdateCallback';
import { window } from './globals';
import { useIsFeatureEnabled, useIsSecurityEnabled } from './helpers/features';
import { useHasPermission } from './shared/permissions';

// NOTE: the `?` means path/path2 also matches w/ path//path2
const Routes = () => {
  //  Feature flags
  const isPackageInventoryEnabled = useIsFeatureEnabled(PACKAGE_INVENTORY);
  const isRawVulnEditEnabled = useIsFeatureEnabled(RAW_VULN_EDIT);
  const isArchiveAndLicenseUploadEnabled = useIsFeatureEnabled(ARCHIVE_AND_LICENSE_UPLOAD);
  const isBinaryDecompositionEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);
  const isSBOMPortalPrivateEnabled = useIsFeatureEnabled(SBOM_PORTAL_PRIVATE);
  const isSecurityEnabled = useIsSecurityEnabled();
  const isApiWebhookNotificationsEnabled = useIsFeatureEnabled(API_WEBHOOK_NOTIFICATIONS);
  const isTeamGroupsEnabled = useIsFeatureEnabled(TEAM_GROUPS);
  const isSbomSharingEnabled = useIsFeatureEnabled(SBOM_SHARING);
  const isInventoryUIEnabled = useIsFeatureEnabled(INVENTORY_UI);
  const isSnippetScanningEnabled = useIsFeatureEnabled(SNIPPET_DETECTION);
  const isVendoredDependenciesEnabled = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);
  const isLicenseConcludedEnabled = useIsFeatureEnabled(LICENSE_CONCLUDED);
  const isReportsV3NewUXFlowEnabled = useIsFeatureEnabled(REPORTS_V3_NEW_UX_FLOW);
  const hasPermission = useHasPermission();
  const permissions = window.user?.permissions || [];
  const isProjectReportsEnabled = userHasPermission(permissions, ResourceType.ProjectReportAny, Action.Create)
    || userHasPermission(permissions, ResourceType.ProjectReport, Action.Create);

  const isReleaseGroupReportsEnabled = userHasPermission(permissions, ResourceType.ReleaseGroupReportAny, Action.Create)
    || userHasPermission(permissions, ResourceType.ReleaseGroupReport, Action.Create);

  return (
    <Router history={browserHistory} onUpdate={() => window.Appcues?.page()}>
      <Route path="/auth" component={Auth}>
        <IndexRedirect to="login" />
        <Route path="login" component={AuthLogin} />
        <Route path="register" component={AuthRegister} />
        <Route path="password-reset" component={AuthPasswordReset} />
      </Route>
      <Route path="/onboarding" component={AuthOnboarding} />
      <Route component={Application}>
        <Route path="/" component={Dashboard}>
          <IndexRedirect to="projects" />
          <Route path="issues" component={IssuesContainer}>
            {/* Global Issues Inbox */}
            <IndexRedirect to="licensing" />
            <Redirect from="security" to="vulnerability" />
            <Route path="overview" component={IssueOverview} />
            <Route path=":category" component={IssuesInbox} />
            <Route path=":category/exceptions" component={IssueExceptionsPage} />
            <Redirect from="security/:id" to="vulnerability/:id" />
            <Route path=":category/:id" component={IssueDetails} />
          </Route>

          {isPackageInventoryEnabled && (
            <Route path="/packages" component={PackageInventoryContainer}>
              <Route path=":packageLocator" />
              <Route path=":packageLocator/:version" component={PackageVersionContainer}>
                <IndexRoute component={ProjectsTable} />
                <Route path="projects" component={ProjectsTable} />
                {isSecurityEnabled && <Route path="vulnerabilities" component={VulnerabilitiesTable} />}
              </Route>
            </Route>
          )}

          {/* Global Reports */}
          <Route path="reports" component={ReportsDashboard} />

          {/*
            Audit Logs

            Note that these top-level paths have been deprecated in favor of the of
            the nested settings routes. We should probably add logging or something to see
            how frequently these are actually used.
          */}
          <Redirect from="audit-logs" to="account/settings/audit-logs" />
          <Redirect from="audit-logs/users" to="account/settings/audit-logs/users" />
          <Redirect from="audit-logs/issues" to="account/settings/audit-logs/issues" />

          {/* Release Groups Dashboard */}
          <Route path="release-groups" component={ProjectDashboard} />

          {isSbomSharingEnabled && (
            <Route path="shared-projects">
              <IndexRoute component={ProjectDashboard} />
            </Route>
          )}

          {/* Projects Dashboard */}
          <Route path="projects">
            <IndexRoute component={ProjectDashboard} />
            {/* Release Group Creation */}
            <Route path="group/create" component={CreateGroupContainer} />

            {/* Release Group Summary */}
            <Route path="group/:projectGroupId" component={ProjectGroupContainer}>
              <IndexRoute component={ProjectGroupReleaseSummary} />
              <Route path="releases" component={ReleaseGroupReleasesList} />
              {/* Release Groups Revamp - edit and clone release pages */}
              <Route path="releases/:releaseId">
                <IndexRedirect to="edit" />
                <Route path="edit" component={EditReleaseContainer} />
                <Route path="clone" component={CloneReleaseContainer} />
              </Route>
              <Route path="projects" component={ProjectGroupProjectsTab} />
              <Route path="issues" component={ReleaseGroupIssuesContainer}>
                {/* Release Group Issues Inbox */}
                <IndexRedirect to="licensing" />
                <Redirect from="security" to="vulnerability" />
                <Route path=":category" component={IssuesInbox} />
                <Route path=":category/exceptions" component={IssueExceptionsPage} />
                <Route path=":category/compare(/:compareReleaseId)" component={IssueComparisonContainer} />
                <Redirect from="security/:id" to="vulnerability/:id" />
                <Route path=":category/:id" component={IssueDetails} />
              </Route>
              {/* Release Group Settings */}
              <Route path="settings" component={ReleaseGroupsSettings}>
                <IndexRedirect to="general" />
                <Route path="general" component={ReleaseGroupsSettingsGeneral} />
                {isSBOMPortalPrivateEnabled && (
                  <Route path="portal" component={ReleaseGroupsSettingsPortal} />
                )}
              </Route>

              {/* Release Group Dependencies */}
              <Route component={ProjectGroupDependencyValidityContainer}>
                <Redirect from="export" to="reports" />
                <Route path="browse">
                  <Route path="licenses" component={ProjectGroupLicenses} />
                  {isInventoryUIEnabled ? (
                    <Route component={ReleaseGroupInventoryBrowseContainer}>
                      <Route path="dependencies" component={DependenciesV2} />
                      <Route path="vendored" component={DependenciesV2} />
                      <Route path="binary" component={BinaryAnalysisContainer} />
                    </Route>
                  ) : (
                    <Route path="dependencies" component={DependenciesV2} />
                  )}
                </Route>
                {isReleaseGroupReportsEnabled && isReportsV3NewUXFlowEnabled && (
                  <Route path="reports" component={ReportsV3Container}>
                    <IndexRedirect to="licensing" />
                    <Route path=":reportType" component={ReportsV3Page} />
                  </Route>
                )}
                {isReleaseGroupReportsEnabled && !isReportsV3NewUXFlowEnabled && (
                  <Route path="reports" component={ProjectGroupReportContainer}>
                    <IndexRedirect to="licensing" />
                    <Route path="settings" component={ReleaseGroupReportGenerationSettings} />
                    <Route path=":report_type" component={ReportGeneration} />
                  </Route>
                )}
              </Route>
            </Route>

            {/* Project Import - Upload */}
            <Route path="import/upload">
              {isArchiveAndLicenseUploadEnabled && <Route path="archive" component={ArchiveUploadPage} />}
              <Route path="sbom" component={(router) => <SBOMUploadPage router={router} />} />
              {isBinaryDecompositionEnabled && <Route path="binary" component={BinaryUploadPage} />}
              <IndexRedirect to="archive" />
            </Route>

            {/* Project Import - Bulk */}
            <Route path="import(/:service)" component={BulkImportPage} />

            {/* Project Summary */}
            <Route component={ProjectProvider}>
              <Route path=":project_id" component={ProjectContainer}>
                <IndexRoute component={ProjectEmpty} />

                {/* Project Settings */}
                <Route path="settings" component={ProjectSettingsV2Container}>
                  <IndexRoute component={ProjectSettingsV2General} />
                  {/* Legacy redirects to handle old route names */}
                  <Redirect from="languages(/:language)" to="builds" />
                  <Redirect from="hooks" to="update-hooks" />

                  <Route path="policies" component={ProjectSettingsV2Policies} />
                  <Redirect from="issues" to="policies" />
                  <Route path="issue-tracker" component={ProjectSettingsV2IssueTracker} />
                  <Route path="notifications" component={ProjectSettingsV2Notifications} />
                  <Route path="update-hooks" component={ProjectSettingsV2UpdateHooks} />
                  <Route path="builds" component={ProjectSettingsV2Builds} />
                </Route>

                {/* Project History */}
                <Route path="history">
                  <Route path="builds" component={ProjectBuildsPage} />
                  <IndexRedirect to="builds" />
                </Route>

                {/* Project Summary */}
                <Route path="refs/:reference_type/:reference" component={ProjectOverviewPage}>
                  <Route path=":revision_id" component={RevisionContainer}>
                    <IndexRoute component={ProjectSummary} />
                    <Route path="preview" component={ProjectPreview} />
                    {isInventoryUIEnabled ? (
                      <Route path="browse" component={ProjectInventoryBrowseContainer}>
                        <Route path="dependencies" component={DependenciesV2} />
                        {isSnippetScanningEnabled && <Route path="snippets" component={SnippetsContainer} />}
                        {isBinaryDecompositionEnabled && <Route path="binary" component={BinaryAnalysisContainer} />}
                        {isVendoredDependenciesEnabled && <Route path="vendored" component={VendoredDependenciesContainer} />}
                      </Route>
                    ) : (
                      <Route path="browse">
                        <Route path="dependencies" component={DependenciesV2} />
                      </Route>
                    )}
                    {isSnippetScanningEnabled && (
                      <Route path="browse/snippets">
                        {/* We explicitly render this OUTSIDE the inventory/browse container because it */}
                        {/* needs to take up the entire width of the page/can't be rendered inside the SidebarNavLayout */}
                        <Route path="compare/:status" component={SnippetsCompareContainer} />
                        <Route path=":id" component={FullPageSnippetDetails} />
                      </Route>

                    )}
                    <Route component={RevisionReadyContainer}>
                      <Route path="browse">
                        <Route path="licenses" component={ProjectBrowseLicensesPage} />
                      </Route>
                      <Redirect from="export" to="reports" />
                      {isProjectReportsEnabled && isReportsV3NewUXFlowEnabled && (
                        <Route path="reports" component={ReportsV3Container}>
                          <IndexRedirect to="licensing" />
                          <Route path=":reportType" component={ReportsV3Page} />
                        </Route>
                      )}
                      {isProjectReportsEnabled && !isReportsV3NewUXFlowEnabled && (
                        //  Old flow
                        <Route path="reports" component={ProjectReportContainer}>
                          <IndexRedirect to="licensing" />
                          <Route path="settings" component={ProjectReportGenerationSettings} />
                          <Route path=":report_type" component={ReportGeneration} />
                        </Route>
                      )}
                      <Route path="issues" component={IssuesContainer}>
                        {/* Project Issues Inbox */}
                        <IndexRedirect to="licensing" />
                        <Redirect from="security" to="vulnerability" />
                        <Route path=":category" component={ProjectIssuesRouteContainer} />
                        <Route path=":category/exceptions" component={IssueExceptionsPage} />
                        <Route path=":category/compare(/:compareRevisionId)" component={IssueComparisonContainer} />
                        <Redirect from="security/:id" to="vulnerability/:id" />
                        <Route path=":category/:id" component={IssueDetails} />
                      </Route>
                    </Route>
                  </Route>
                </Route>
                <Route path="see-more" component={AccessGate} />
              </Route>
            </Route>
          </Route>

          {/* Org-level Settings */}
          <Route path="account/settings" component={SettingsContainer}>
            {/* Account settings */}
            <IndexRoute component={LoginOrganizationSettings} />

            {/* Organization Settings */}
            <Route path="organization" component={OrganizationSettings}>
              <IndexRoute component={OrganizationGeneralSettings} />
              <FeatureFlagRequired flagName={SBOM_PORTAL}>
                <Route path="portal" component={SettingsOrganizationPortalConfiguration} />
                <Route path="portal/tokens" component={SettingsOrganizationPortalTokens} />
              </FeatureFlagRequired>
              <Route path="users" component={OrganizationUsersIndex} />
              <Route path="users/:id" component={EditUserSettings} />
              {isTeamGroupsEnabled && <Route path="teams/groups/:id" component={TeamGroupManagement} />}
              <Route path="teams" component={OrganizationTeamsIndex} />
              <Route path="teams/:id" component={TeamSettings} />
              <Route path="roles" component={OrganizationRolesIndex} />
              <Route path="roles/clone/:roleId" component={OrganizationRolesEdit} />
              <Route path="roles/:id" component={OrganizationRolesEdit} />
              {window.on_prem && <Route path="banners" component={OrganizationBanners} />}
              <Route path="authentication" component={AuthenticationSettings} />
              {/* Redirect the legacy project routes to their new settings */}
              <Redirect from="project" to="/account/settings/projects" />
              <Redirect from="project/issues" to="/account/settings/projects/issues" />
              <Redirect from="project/notifications" to="/account/settings/projects/notifications" />
              <Redirect from="project/hooks" to="/account/settings/projects/hooks" />
            </Route>

            {/* Global Project settings */}
            <Route path="projects" component={SettingsOrganizationProject}>
              <IndexRedirect to="general" />
              <Route path="general" component={SettingsOrganizationProjectGeneral} />
              <Route path="issues" component={SettingsOrganizationProjectIssues} />
              <Route path="notifications" component={SettingsOrganizationProjectNotifications} />
              <Route path="hooks" component={SettingsOrganizationProjectUpdateHooks} />
            </Route>

            {/* Integration Settings */}
            <Route path="integrations" component={IntegrationSettings}>
              <Route path="jira" component={JiraOrganizationSettings} />
              <Route path="github" component={GithubSettings} />
              <Route path="slack" component={SlackSettings} />
              <Route path="bitbucket" component={BitbucketSettings} />
              <Route path="gitlab" component={GitlabSettings} />
              <Route path="api_tokens" component={APIGranularTokenSettings} />
              <Route path="api_user" component={CreateApiUserSettings} />
              <Route path="oidc" component={OIDCProviderSettings} />
              {isApiWebhookNotificationsEnabled && (
                <Route path="api_webhooks" component={ApiWebhookSettings} />
              )}
              <Route path="custom_license_scans" component={CustomLicenseScanSettings} />
              <IndexRedirect
                to={
                  (hasPermission({
                    action: Action.Edit,
                    resourceType: ResourceType.OrganizationIntegrationSettings,
                  }) && !isFreeTier({
                    plan: window.user?.organization?.stripe_plan,
                    stripePriceLimit: window.user?.organization?.stripePriceLimit,
                  }))
                    ? 'jira'
                    : 'github'
                }
              />
            </Route>

            {/* Language Settings */}
            <Route path="languages" component={LanguageSettings}>
              <IndexRedirect to="java" />
              <Route path="java" component={JavaLanguagesSettings} />
              <Route path="npm" component={JavaScriptLanguagesSettings} />
              <Route path="bower" component={BowerLanguageSettings} />
              <Route path="cocoa" component={CocoaLanguageSettings} />
              <Route path="ruby" component={GemLanguageSettings} />
              <Route path="python" component={PipLanguageSettings} />
              <Route path="dotnet" component={DotnetLanguageSettings} />
              <Route path="git" component={GitLanguageSettings} />
              <Route path="pub" component={PubLanguagesSettings} />
            </Route>

            {/* Billing */}
            <Route path="billing" component={SubscriptionSettings} />
            <Route path="billing/callback" component={PlanUpdateCallback} />

            {/* Audit Logs */}
            <Route path="audit-logs" component={AuditLogs}>
              <IndexRoute component={AuditLogsPage} />
              <Route path="users" component={AuditLogsPage} />
              <Route path="issues" component={AuditLogsPage} />
            </Route>
          </Route>

          {/* Policies */}
          <Route path="policies" component={ListPoliciesPage}>
            <Route path="licensing" component={ListLicensePoliciesPage} />
            <Route path="licensing/:policyId" component={DisplayLicensePolicyPageV2 as RouteComponent} />
            {isLicenseConcludedEnabled && (
              <Route path="licensing/:policyId/settings" component={DisplayLicensePolicyRulesPage as RouteComponent} />
            )}
            <Route path="security" component={ListSecurityPoliciesPage} />
            <Route path="security/:policyId" component={DisplaySecurityPolicyPage} />
            <Redirect from="vulnerability" to="security" />
            <Redirect from="vulnerability/:policyId" to="security/:policyId" />
            <Route path="quality" component={ListQualityPoliciesPage} />
            <Route path="quality/:policyId" component={DisplayQualityPolicyPage} />
            <Route path="sbom" component={ListSbomPoliciesPage} />
            <Route path="sbom/:policyId" component={DisplaySBOMPolicyPage} />
            <IndexRedirect to="licensing" />
          </Route>

          {/* Join Organization Page */}
          <Route path="account/join-org" component={JoinOrganization} />

          {/* Ensure Webhook */}
          <Route path="services/:service/ensureWebHook" component={EnsureWebhook} />

          {/* Admin Pages */}
          {window.user?.super && <Route path="admin" component={AdminDashboard} />}
          {window.user?.super && <Route path="internal-tools" component={InternalToolsPage} />}
          {isRawVulnEditEnabled && <Route path="raw-vulns" component={RawVulnEditPage} />}

          {/* Not Found */}
          <Route path="*" component={NotFound} />
        </Route>
      </Route>
    </Router>
  );
};

export default Routes;
